import { useCallback, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  useTransportMutation,
  useTransportPaginatedQuery,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"
import type { AgentRecord } from "@/types"
import { useQueryClient } from "@tanstack/react-query"

import { invalidateHomeQueries } from "@/lib/home-query-cache"
import type { AgentResource } from "@/types_api/backend/transport/agent_resource"
import type { CreateAgentRequest } from "@/types_api/backend/transport/create_agent_request"
import type { ListAgentsRequest } from "@/types_api/backend/transport/list_agents_request"
import type { TestAgentConnectionResponse } from "@/types_api/backend/transport/test_agent_connection_response"
import type { UpdateAgentRequest } from "@/types_api/backend/transport/update_agent_request"
import type { FormData } from "@/types/FormData"
import { asJsonObjectValueOrNull, type JsonObject } from "@/types/json"

const AGENTS_PAGE_SIZE = 1000
const ACTIVE_AGENTS_REQUEST: ListAgentsRequest = {
  page_size: AGENTS_PAGE_SIZE,
}
const ALL_AGENTS_REQUEST: ListAgentsRequest = {
  include_inactive: true,
  page_size: AGENTS_PAGE_SIZE,
}
type TransportQueryUtils = ReturnType<typeof useTransportQueryUtils>
type AgentResourceWithTags = AgentResource & {
  tags?: string[] | null
}
type UpdateAgentInput = Omit<
  Partial<FormData>,
  "apply_linked_metrics_to_conversation_uploads"
> & {
  tags?: UpdateAgentRequest["tags"]
}
function normalizeJsonObject(value: unknown): JsonObject {
  return asJsonObjectValueOrNull(value) ?? {}
}
function optionalMetadataField(
  value: unknown,
): { metadata: JsonObject } | Record<string, never> {
  if (value === undefined) {
    return {}
  }
  const normalized = asJsonObjectValueOrNull(value)
  if (normalized === null) {
    return {}
  }
  return { metadata: normalized }
}
function mapAgentResource(agent: AgentResourceWithTags): AgentRecord {
  return {
    ...agent,
    metadata: asJsonObjectValueOrNull(agent.metadata),
    attributes: asJsonObjectValueOrNull(agent.attributes),
    workflows: normalizeJsonObject(agent.workflows) as AgentRecord["workflows"],
    knowledge_base_ids: agent.knowledge_base_ids ?? [],
    tags: agent.tags ?? [],
  } as AgentRecord
}
export function getNextAgentsRequest(
  lastPage: { next_page_token?: string | null },
  previousRequest: ListAgentsRequest | undefined,
): ListAgentsRequest | null {
  if (!lastPage.next_page_token || !previousRequest) {
    return null
  }

  return {
    ...previousRequest,
    page_token: lastPage.next_page_token,
  }
}
async function invalidateAgentTransportQueries({
  agentId,
  includeHomeQueries = false,
  queryClient,
  refetchLists = true,
  transportQueryUtils,
}: {
  agentId?: string
  includeHomeQueries?: boolean
  queryClient: ReturnType<typeof useQueryClient>
  refetchLists?: boolean
  transportQueryUtils: TransportQueryUtils
}) {
  const listRefetchType = refetchLists ? "active" : "none"

  await Promise.allSettled([
    transportQueryUtils.invalidatePaginatedOperation("listAgents", {
      refetchType: listRefetchType,
    }),
    transportQueryUtils.invalidatePaginatedOperation("listAllAgents", {
      refetchType: listRefetchType,
    }),
    ...(agentId
      ? [
          transportQueryUtils.invalidate("getAgent", { agent_id: agentId }),
          transportQueryUtils.invalidate("getAgentVersions", {
            agent_id: agentId,
          }),
        ]
      : []),
    ...(includeHomeQueries ? [invalidateHomeQueries(queryClient)] : []),
  ])
}
const AGENT_LIST_OPERATIONS = ["listAgents", "listAllAgents"] as const
/** Drops an agent from cached agent lists without going to the network. */
function removeAgentFromCachedLists({
  agentId,
  transportQueryUtils,
}: {
  agentId: string
  transportQueryUtils: TransportQueryUtils
}) {
  for (const operation of AGENT_LIST_OPERATIONS) {
    transportQueryUtils.updatePaginatedOperationData(
      operation,
      (agents: AgentRecord[] | undefined) =>
        agents?.filter((agent) => agent.id !== agentId),
    )
  }
}

export function buildUpdateAgentRequest(
  agentId: string,
  input: UpdateAgentInput,
): UpdateAgentRequest {
  return {
    agent_id: agentId,
    ...(input.display_name !== undefined
      ? { display_name: input.display_name }
      : {}),
    ...(input.model_type !== undefined ? { model_type: input.model_type } : {}),
    ...(input.phone_number !== undefined
      ? { phone_number: input.phone_number }
      : {}),
    ...(input.endpoint !== undefined ? { endpoint: input.endpoint } : {}),
    ...optionalMetadataField(input.metadata),
    ...(input.attributes !== undefined
      ? { attributes: normalizeJsonObject(input.attributes) }
      : {}),
    ...(input.workflows !== undefined
      ? { workflows: normalizeJsonObject(input.workflows) }
      : {}),
    ...(input.prompt !== undefined ? { prompt: input.prompt } : {}),
    ...(input.language !== undefined ? { language: input.language } : {}),
    ...(input.metric_ids !== undefined ? { metric_ids: input.metric_ids } : {}),
    ...(input.tags !== undefined
      ? { tags: input.tags as UpdateAgentRequest["tags"] }
      : {}),
  }
}

export function useAgentsListTransport({
  includeInactive,
  enabled,
  refetchOnMount,
  tagFilters,
}: {
  includeInactive?: boolean
  enabled?: boolean
  // Per-observer, so a caller that needs the list fresh revalidates on mount
  // without changing the lookup cache profile for everyone else reading it.
  refetchOnMount?: "always" | boolean
  tagFilters?: string[]
} = {}) {
  const request = useMemo<ListAgentsRequest>(
    () => ({
      ...(includeInactive ? ALL_AGENTS_REQUEST : ACTIVE_AGENTS_REQUEST),
      ...(tagFilters && tagFilters.length > 0
        ? { tag_filters: tagFilters }
        : {}),
    }),
    [includeInactive, tagFilters],
  )

  return useTransportPaginatedQuery({
    operation: includeInactive ? "listAllAgents" : "listAgents",
    request,
    combinePages: (pages) =>
      pages.flatMap((page) => page.agents.map(mapAgentResource)),
    getNextRequest: getNextAgentsRequest,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      ...(refetchOnMount === undefined ? {} : { refetchOnMount }),
    },
  })
}

export function useAgentTransport(agentId?: string) {
  return useTransportQuery({
    operation: "getAgent",
    request: { agent_id: agentId ?? "" },
    options: {
      enabled: !!agentId,
      select: (response) => mapAgentResource(response.agent),
    },
  })
}

export function useAgentNamesTransport(agentIds: string[]) {
  const request = useMemo(
    () => ({
      agent_ids: agentIds,
    }),
    [agentIds],
  )

  return useTransportQuery({
    operation: "getAgentNames",
    request,
    options: {
      enabled: agentIds.length > 0,
      gcTime: 10 * 60 * 1000,
      select: (response) => response.names ?? {},
    },
  })
}

export function useAgentTransportInvalidation() {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(
    (options?: { agentId?: string; includeHomeQueries?: boolean }) =>
      invalidateAgentTransportQueries({
        agentId: options?.agentId,
        includeHomeQueries: options?.includeHomeQueries,
        queryClient,
        transportQueryUtils,
      }),
    [queryClient, transportQueryUtils],
  )
}

/**
 * Revalidates the agent catalog for a change this tab's query cache cannot
 * see: a creation form opened in another tab invalidates that tab's cache, not
 * this one, and the catalog refetches on neither mount nor focus.
 */
export function useRevalidateAgentsCatalog() {
  const invalidateAgents = useAgentTransportInvalidation()

  return useCallback(() => void invalidateAgents(), [invalidateAgents])
}

export function useAgentTestConnectionTransport(options?: {
  onSuccess?: (data: TestAgentConnectionResponse) => void
}) {
  return useTransportMutation("testAgentConnection", options)
}

export function useAgentTransportMutations() {
  const transportQueryUtils = useTransportQueryUtils()
  const createAgentMutation = useTransportMutation("createAgent")
  const updateAgentMutation = useTransportMutation("updateAgent")
  const deleteAgentMutation = useTransportMutation("deleteAgent", {
    onMutate: () => transportQueryUtils,
    onSuccess: (_response, variables, originalQueryUtils) => {
      if (originalQueryUtils) {
        removeAgentFromCachedLists({
          agentId: variables.agent_id,
          transportQueryUtils: originalQueryUtils,
        })
      }
    },
  })
  const duplicateAgentMutation = useTransportMutation("duplicateAgent")
  const addAgentMetricsMutation = useTransportMutation("addAgentMetrics")
  const removeAgentMetricMutation = useTransportMutation("removeAgentMetric")
  const setAgentMetricsMutation = useTransportMutation("setAgentMetrics")
  const addAgentWorkflowMutation = useTransportMutation("addAgentWorkflow")
  const removeAgentWorkflowMutation = useTransportMutation(
    "removeAgentWorkflow",
  )

  const createAgent = useCallback(
    async (input: CreateAgentRequest) => {
      const response = await createAgentMutation.mutateAsync(input)
      return mapAgentResource(response.agent)
    },
    [createAgentMutation],
  )

  const updateAgent = useCallback(
    async (agentId: string, input: UpdateAgentInput) => {
      const response = await updateAgentMutation.mutateAsync(
        buildUpdateAgentRequest(agentId, input),
      )
      return mapAgentResource(response.agent)
    },
    [updateAgentMutation],
  )

  const deleteAgent = useCallback(
    async (input: { id: string }) => {
      await deleteAgentMutation.mutateAsync({ agent_id: input.id })
    },
    [deleteAgentMutation],
  )

  const duplicateAgent = useCallback(
    async (input: { id: string; includeAssociations?: boolean }) => {
      const response = await duplicateAgentMutation.mutateAsync({
        agent_id: input.id,
        include_associations: input.includeAssociations,
      })
      return {
        id: response.id,
        agent: mapAgentResource(response.agent),
      }
    },
    [duplicateAgentMutation],
  )

  const addAgentMetrics = useCallback(
    (input: { agentId: string; metricIds: string[] }) =>
      addAgentMetricsMutation.mutateAsync({
        agent_id: input.agentId,
        metric_ids: input.metricIds,
      }),
    [addAgentMetricsMutation],
  )

  const removeAgentMetric = useCallback(
    (input: { agentId: string; metricId: string }) =>
      removeAgentMetricMutation.mutateAsync({
        agent_id: input.agentId,
        metric_id: input.metricId,
      }),
    [removeAgentMetricMutation],
  )

  const setAgentMetrics = useCallback(
    (input: { agentId: string; metricIds: string[] }) =>
      setAgentMetricsMutation.mutateAsync({
        agent_id: input.agentId,
        metric_ids: input.metricIds,
      }),
    [setAgentMetricsMutation],
  )

  const addAgentWorkflow = useCallback(
    (input: { agentId: string; workflowId: string; workflow: unknown }) =>
      addAgentWorkflowMutation.mutateAsync({
        agent_id: input.agentId,
        workflow_id: input.workflowId,
        workflow: normalizeJsonObject(input.workflow),
      }),
    [addAgentWorkflowMutation],
  )

  const removeAgentWorkflow = useCallback(
    (input: { agentId: string; workflowId: string }) =>
      removeAgentWorkflowMutation.mutateAsync({
        agent_id: input.agentId,
        workflow_id: input.workflowId,
      }),
    [removeAgentWorkflowMutation],
  )

  return useMemo(
    () => ({
      addAgentMetrics,
      addAgentWorkflow,
      createAgent,
      deleteAgent,
      duplicateAgent,
      isAddingAgentMetrics: addAgentMetricsMutation.isPending,
      isCreatingAgent: createAgentMutation.isPending,
      isDeletingAgent: deleteAgentMutation.isPending,
      isDuplicatingAgent: duplicateAgentMutation.isPending,
      isUpdatingAgent: updateAgentMutation.isPending,
      removeAgentMetric,
      removeAgentWorkflow,
      setAgentMetrics,
      updateAgent,
      updateAgentError: updateAgentMutation.error,
    }),
    [
      addAgentMetrics,
      addAgentWorkflow,
      addAgentMetricsMutation.isPending,
      createAgent,
      createAgentMutation.isPending,
      deleteAgent,
      deleteAgentMutation.isPending,
      duplicateAgent,
      duplicateAgentMutation.isPending,
      removeAgentMetric,
      removeAgentWorkflow,
      setAgentMetrics,
      updateAgent,
      updateAgentMutation.error,
      updateAgentMutation.isPending,
    ],
  )
}
